import React, { useState, useEffect } from "react";
import {
  Home,
  Wrench,
  Building2,
  Users,
  Award,
} from "lucide-react";
import settingsService from "./services/settingsService";

function AboutUs() {
  const [aboutData, setAboutData] = useState({
    business_name: "VJ Dsign Studio",
    tagline: "Designing Spaces With Purpose",
    description:
      "VJ Dsign Studio specializes in Interior Design, Residential Projects, and Renovation Works. We collaborate with direct clients, architects, and engineers to deliver functional, elegant, and customized spaces that reflect every client's vision.",
    experience_year: "Since 2017",
    experience_text: "Creating Beautiful Spaces With Excellence",
  });

  useEffect(() => {
    async function loadAboutSettings() {
      try {
        const general = await settingsService.getSetting("general");
        const all = await settingsService.getAll();
        if (general || all) {
          setAboutData((prev) => ({
            ...prev,
            business_name: general?.business_name || all?.business_name || prev.business_name,
            tagline: general?.tagline || all?.tagline || prev.tagline,
          }));
        }
      } catch (err) {
        // Fallback safely
      }
    }
    loadAboutSettings();
  }, []);

  const features = [
    {
      icon: <Home className="w-6 h-6 sm:w-7 sm:h-7" />,
      title: "Residential Interiors",
    },
    {
      icon: <Wrench className="w-6 h-6 sm:w-7 sm:h-7" />,
      title: "Renovation & Remodeling",
    },
    {
      icon: <Building2 className="w-6 h-6 sm:w-7 sm:h-7" />,
      title: "Architectural Coordination",
    },
    {
      icon: <Users className="w-6 h-6 sm:w-7 sm:h-7" />,
      title: "Working with Clients, Architects & Engineers",
    },
  ];

  return (
    <>
      <section
        id="about-us"
        className="relative overflow-hidden py-16 md:py-24 bg-gradient-to-b from-white via-slate-50/50 to-white"
      >
        {/* Animated Aurora Glows */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute -top-20 -right-20 w-72 sm:w-[450px] h-72 sm:h-[450px] bg-[#9BB7A3]/10 rounded-full blur-[80px] sm:blur-[120px] animate-blob animate-float-slow"></div>
          <div className="absolute -bottom-20 -left-20 w-72 sm:w-[500px] h-72 sm:h-[500px] bg-slate-200/60 rounded-full blur-[90px] sm:blur-[140px] animate-blob animate-float-reverse-slow"></div>
          <div className="absolute top-1/3 left-1/4 w-60 h-60 bg-[#9BB7A3]/5 rounded-full blur-[100px] hidden sm:block"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <span className="text-[#9BB7A3] font-bold tracking-[3px] sm:tracking-[4px] uppercase text-xs sm:text-sm">
              About Us
            </span>

            <h2 className="text-2xl sm:text-3xl md:text-5xl font-extrabold text-slate-800 mt-3 sm:mt-4 px-2">
              {aboutData.tagline}
            </h2>

            <div className="w-16 sm:w-24 h-1 bg-[#9BB7A3] mx-auto mt-4 sm:mt-6 rounded-full"></div>
          </div>

          {/* Outer Grid Layout */}
          <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-4">
                {aboutData.business_name}
              </h3>

              <p className="text-slate-600 text-sm sm:text-base md:text-lg leading-relaxed mb-6 sm:mb-8 max-w-2xl mx-auto lg:mx-0">
                {aboutData.description}
              </p>

              {/* Experience Card */}
              <div className="flex flex-col sm:flex-row items-center gap-4 bg-[#9BB7A3]/10 p-4 sm:p-5 rounded-2xl border border-[#9BB7A3]/20 text-center sm:text-left max-w-md mx-auto lg:mx-0 bg-white/40 backdrop-blur-sm">
                <div className="p-3 bg-white rounded-xl shadow-sm border border-[#9BB7A3]/20">
                  <Award className="text-[#9BB7A3]" size={32} />
                </div>
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-slate-800">
                    {aboutData.experience_year}
                  </h4>
                  <p className="text-slate-600 text-xs sm:text-sm mt-0.5">
                    {aboutData.experience_text}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Features Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {features.map((item, index) => (
                <div
                  key={index}
                  className="
                    bg-white/80
                    backdrop-blur-md
                    p-5 sm:p-6
                    rounded-2xl sm:rounded-3xl
                    shadow-sm
                    hover:shadow-xl
                    border
                    border-slate-100
                    hover:border-[#9BB7A3]/40
                    transition-all
                    duration-300
                    hover:-translate-y-1
                    md:hover:-translate-y-2
                    flex flex-col items-center text-center sm:items-start sm:text-left
                  "
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-[#9BB7A3]/10 flex items-center justify-center text-[#9BB7A3] mb-3 sm:mb-4 transition-transform duration-300 group-hover:scale-110">
                    {item.icon}
                  </div>

                  <h3 className="font-bold text-sm sm:text-base text-slate-800 leading-snug sm:leading-relaxed">
                    {item.title}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <style>
        {`
          @keyframes floatSlow {
            0% { transform: translateY(0px) scale(1); }
            50% { transform: translateY(-25px) scale(1.05); }
            100% { transform: translateY(0px) scale(1); }
          }
          @keyframes floatReverseSlow {
            0% { transform: translateY(0px) scale(1.05); }
            50% { transform: translateY(25px) scale(1); }
            100% { transform: translateY(0px) scale(1.05); }
          }
          .animate-float-slow {
            animation: floatSlow 8s ease-in-out infinite;
          }
          .animate-float-reverse-slow {
            animation: floatReverseSlow 10s ease-in-out infinite;
          }
        `}
      </style>
    </>
  );
}

export default AboutUs;